import StorefrontIcon from "@mui/icons-material/Storefront";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import InventoryIcon from "@mui/icons-material/Inventory";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SpeedIcon from "@mui/icons-material/Speed";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useAppTrialAccess } from "../useAppTrialAccess";
import ActivarSuscripcionBanner from "../ActivarSuscripcionBanner";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import '../premiumLanding.css';

export default function RetailPOSLandingPage({ showMyStoresButton = false, onEnterStores, showLoading = false }) {
  const navigate = useNavigate();
  const { isAuthenticated, canRequestTrial, mustRenew, startTrial } =
    useAppTrialAccess("retail_pos_v1");

  if (showLoading) {
    return (
      <div className="pl-loading">
        <CircularProgress style={{ color: '#183B66' }} />
      </div>
    );
  }

  const features = [
    {
      icon: <InventoryIcon />,
      titulo: "Inventario en tiempo real",
      desc: "Control total de stock: cada venta actualiza tu inventario automáticamente.",
    },
    {
      icon: <QrCodeScannerIcon />,
      titulo: "Código de barras",
      desc: "Escanea y vende en segundos. Compatible con cualquier lector USB.",
    },
    {
      icon: <ShoppingCartIcon />,
      titulo: "POS rápido",
      desc: "Punto de venta optimizado para teclado. Menos clicks, más ventas.",
    },
    {
      icon: <BarChartIcon />,
      titulo: "Reportes de negocio",
      desc: "Ventas del día, del mes, productos más vendidos y alertas de stock.",
    },
    {
      icon: <StorefrontIcon />,
      titulo: "Catálogo online",
      desc: "Tu tienda en /store/tu-slug con stock en vivo y pedidos por WhatsApp.",
    },
    {
      icon: <PhotoLibraryIcon />,
      titulo: "Fotos de producto",
      desc: "Sube imágenes a tu inventario y muéstralas en el catálogo público.",
    },
    {
      icon: <SpeedIcon />,
      titulo: "Simple y rápido",
      desc: "Sin complicaciones. Diseñado para negocios pequeños y medianos en LATAM.",
    },
  ];

  return (
    <div className="pl">
      {/* HERO */}
      <section className="pl-hero">
        <div className="pl-wrap">
          <span className="pl-eyebrow">POS para tiendas de ropa</span>
          <h1>POS Retail — Ropa</h1>
          <p className="pl-hero-sub">
            Controla tu inventario, vende con escáner de barras y conoce cómo va tu negocio.{' '}
            <strong>Hecho para tiendas de ropa pequeñas y medianas.</strong>
          </p>
          <div className="pl-cta-row">
            {canRequestTrial && (
              <button type="button" className="pl-btn pl-btn-accent" onClick={startTrial}>
                Probar ahora
              </button>
            )}
            {!isAuthenticated && (
              <button type="button" className="pl-btn pl-btn-accent" onClick={() => navigate('/login')}>
                Inicia sesión para probar gratis
              </button>
            )}
            {showMyStoresButton && (
              <button type="button" className="pl-btn pl-btn-ghost-light" onClick={onEnterStores}>
                Ver mis tiendas
              </button>
            )}
          </div>
          <p className="pl-hero-note">Prueba 30 días con catálogo y fotos incluidas · Desde USD 3 / mes</p>
        </div>
      </section>

      {/* Para quién */}
      <section className="pl-section">
        <div className="pl-wrap">
          <div className="pl-section-head">
            <h2>¿Para quién es este sistema?</h2>
            <p>Diseñado para tiendas de ropa que quieren ordenarse, vender rápido y dejar el cuaderno.</p>
          </div>
          <div className="pl-usecase-grid">
            {[
              "Tiendas de ropa",
              "Boutiques",
              "Tiendas de zapatos",
              "Ropa deportiva",
              "Accesorios y moda",
              "Tiendas multimarca",
            ].map((item) => (
              <div key={item} className="pl-usecase">{item}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="pl-section pl-section-muted">
        <div className="pl-wrap">
          <div className="pl-section-head">
            <h2>Todo lo que necesitas</h2>
            <p>Un solo sistema para inventario, ventas y compras. Sin complicaciones.</p>
          </div>
          <div className="pl-feature-grid">
            {features.map((f) => (
              <div key={f.titulo} className="pl-feature-card">
                <div className="pl-feature-icon">{f.icon}</div>
                <h3>{f.titulo}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pl-section">
        <div className="pl-wrap">
          {canRequestTrial ? (
            <div className="pl-cta-block">
              <h2>Empieza hoy</h2>
              <p>Prueba gratis por 30 días: POS, catálogo web y fotos de producto. Sin tarjeta. Sin compromiso.</p>
              <div className="pl-cta-row">
                <button type="button" className="pl-btn pl-btn-primary" onClick={startTrial}>
                  Activar prueba gratis
                </button>
              </div>
            </div>
          ) : !isAuthenticated ? (
            <div className="pl-cta-block">
              <h2>Empieza hoy</h2>
              <p>Inicia sesión para activar tu prueba gratis de 30 días (POS, catálogo y fotos).</p>
              <div className="pl-cta-row">
                <button type="button" className="pl-btn pl-btn-primary" onClick={() => navigate('/login')}>
                  Iniciar sesión
                </button>
              </div>
            </div>
          ) : (
            <ActivarSuscripcionBanner appKey="retail_pos_v1" appName="POS Retail Ropa" />
          )}
        </div>
      </section>
    </div>
  );
}
